import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inp = "w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[11px] outline-none focus:ring-1 focus:ring-cyan-500";
const ST = { online: "bg-emerald-500", offline: "bg-red-500", unknown: "bg-slate-500" };
const fmt = (v, u = "") => (v == null || v === "" ? "—" : `${v}${u}`);
const ago = (ts) => (ts ? `${Math.max(0, Math.round((Date.now() - new Date(ts)) / 60000))} min temu` : "brak odczytu");

function Spark({ rows, k, color, label, unit }) {
  const v = rows.map((r) => r[k]).filter((x) => typeof x === "number");
  if (v.length < 2) return <div className="text-[10px] text-slate-500">{label}: za mało danych</div>;
  const lo = Math.min(...v), hi = Math.max(...v), W = 260, H = 40;
  const d = v.map((x, i) => `${i ? "L" : "M"}${((i / (v.length - 1)) * W).toFixed(1)},${(H - 2 - ((x - lo) / (hi - lo || 1)) * (H - 4)).toFixed(1)}`).join(" ");
  return (
    <div><div className="text-[10px] text-slate-400">{label}: {lo}…{hi}{unit} (ostatnio {v[v.length - 1]}{unit})</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10 bg-slate-950 rounded" data-testid={`wifi-mon-spark-${k}`}><path d={d} fill="none" stroke={color} strokeWidth="1.5" /></svg></div>
  );
}

function Details({ row }) {
  const [hist, setHist] = useState([]);
  useEffect(() => { api.get(`/wifi/monitoring/${row.id}/history?hours=24`).then((r) => setHist(r.data)).catch(() => {}); }, [row.id, row.live?.ts]);
  const st = row.live?.station_list || [];
  const up = hist.length ? Math.round((100 * hist.filter((h) => h.up).length) / hist.length) : null;
  return (
    <div className="bg-slate-900/70 border-t border-slate-800 p-2 space-y-2" data-testid={`wifi-mon-details-${row.id}`}>
      <div className="grid sm:grid-cols-3 gap-2">
        <Spark rows={hist} k="signal" color="#22d3ee" label="Sygnał 24 h" unit=" dBm" />
        <Spark rows={hist} k="ccq" color="#a78bfa" label="CCQ 24 h" unit="%" />
        <Spark rows={hist} k="stations" color="#10b981" label="Stacje 24 h" unit="" />
      </div>
      <div className="text-[10px] text-slate-400">Dostępność 24 h: {fmt(up, "%")} · odczytów: {hist.length}{row.live?.error ? ` · ${row.live.error}` : ""}</div>
      {st.length > 0 && (
        <table className="w-full text-[10px]" data-testid={`wifi-mon-stations-${row.id}`}>
          <thead><tr className="text-slate-500 text-left"><th>MAC</th><th>Abonent</th><th>Sygnał</th><th>CCQ</th><th>TX/RX Mb/s</th><th>Odl.</th></tr></thead>
          <tbody>{st.map((s) => (
            <tr key={s.mac} className="border-t border-slate-800"><td className="font-mono">{s.mac}</td><td>{s.client_name || <span className="text-slate-500">{s.name || "niedopasowany"}</span>}</td>
              <td className={s.signal < (row.monitoring.alarm_min_signal ?? -75) ? "text-red-400" : ""}>{fmt(s.signal, " dBm")}</td><td>{fmt(s.ccq, "%")}</td>
              <td>{fmt(s.tx_rate)}/{fmt(s.rx_rate)}</td><td>{fmt(s.distance_m, " m")}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function ConfigForm({ dev, profiles, onClose, onSaved }) {
  const m = dev.monitoring || {};
  const [f, setF] = useState({ enabled: true, radio_profile: m.radio_profile || "ubiquiti_airmax", version: m.version || "v2c", snmp_port: m.snmp_port || 161,
    community: "", v3_user: "", v3_auth_key: "", v3_priv_key: "", poll_interval_s: m.poll_interval_s || 300, alarm_min_signal: m.alarm_min_signal ?? -75, alarm_min_ccq: m.alarm_min_ccq ?? 60 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (enabled) => {
    try { await api.put(`/wifi/devices/${dev.id}/monitoring`, { ...f, enabled }); toast.success(enabled ? "Monitoring włączony" : "Monitoring wyłączony"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fld = (k, l, p = {}) => <label className="text-[10px] text-slate-400">{l}<input value={f[k]} onChange={set(k)} className={inp} data-testid={`wifi-mon-f-${k}`} {...p} /></label>;
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-3" data-testid="wifi-mon-config">
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-md p-4 space-y-2 text-[11px]">
        <div className="font-semibold">Monitoring SNMP — {dev.name} ({dev.ip || "brak IP"})</div>
        <label className="text-[10px] text-slate-400">Profil MIB<select value={f.radio_profile} onChange={set("radio_profile")} className={inp} data-testid="wifi-mon-f-profile">{profiles.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] text-slate-400">Wersja<select value={f.version} onChange={set("version")} className={inp} data-testid="wifi-mon-f-version">{["v1", "v2c", "v3"].map((v) => <option key={v}>{v}</option>)}</select></label>
          {fld("snmp_port", "Port", { type: "number" })}
          {f.version !== "v3" ? fld("community", m.enabled ? "Community (puste = bez zmian)" : "Community", { type: "password" }) : <>{fld("v3_user", "Użytkownik v3")}{fld("v3_auth_key", "Klucz auth (SHA)", { type: "password" })}{fld("v3_priv_key", "Klucz priv (AES)", { type: "password" })}</>}
          {fld("poll_interval_s", "Interwał [s]", { type: "number" })}
          {fld("alarm_min_signal", "Alarm: sygnał poniżej [dBm]", { type: "number" })}
          {fld("alarm_min_ccq", "Alarm: CCQ poniżej [%]", { type: "number" })}
        </div>
        <div className="text-[10px] text-slate-500">Urządzenie trafia do wspólnego monitoringu SNMP. Brak odpowiedzi i słaby sygnał automatycznie otwierają zgłoszenie w Awariach.</div>
        <div className="flex justify-end gap-2 pt-1">
          {m.enabled && <button onClick={() => save(false)} className="px-3 py-1 rounded border border-red-700 text-red-300" data-testid="wifi-mon-disable">Wyłącz</button>}
          <button onClick={onClose} className="px-3 py-1 rounded border border-slate-600" data-testid="wifi-mon-cancel">Anuluj</button>
          <button onClick={() => save(true)} className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-500" data-testid="wifi-mon-save">Zapisz</button>
        </div>
      </div>
    </div>
  );
}

function Row({ r, canManage, onConfig, onPolled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const L = r.live || {};
  const poll = async () => { setBusy(true); try { await api.post(`/wifi/devices/${r.id}/monitoring/poll`); onPolled(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false); };
  return (
    <div className="border-b border-slate-800" data-testid={`wifi-mon-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5 text-[11px] cursor-pointer hover:bg-slate-800/50" onClick={() => setOpen(!open)}>
        <span className={`w-2 h-2 rounded-full ${ST[L.status] || ST.unknown}`} data-testid={`wifi-mon-status-${r.id}`} title={L.status} />
        <span className="w-40 truncate font-medium">{r.name}</span><span className="w-24 truncate text-slate-500">{r.site_name || "—"}</span>
        <span className="w-24 font-mono">{r.ip}</span>
        <span className="w-20">{fmt(L.freq, " MHz")}</span><span className="w-20">{fmt(L.signal, " dBm")}</span><span className="w-16">SNR {fmt(L.snr)}</span>
        <span className="w-16">CCQ {fmt(L.ccq, "%")}</span><span className="w-16">stacje {fmt(L.stations)}</span>
        <span className="flex-1 text-slate-500 truncate">{ago(L.ts)}{r.fail_count ? ` · ${r.fail_count} nieudanych` : ""}{L.freq && r.freq_planned && Math.abs(L.freq - r.freq_planned) > 1 ? " · częstotliwość inna niż w projekcie!" : ""}</span>
        <button onClick={(e) => { e.stopPropagation(); poll(); }} disabled={busy} className="p-1 rounded hover:bg-slate-700" title="Odpytaj teraz" data-testid={`wifi-mon-poll-${r.id}`}><RefreshCw size={12} className={busy ? "animate-spin" : ""} /></button>
        {canManage && <button onClick={(e) => { e.stopPropagation(); onConfig(r); }} className="p-1 rounded hover:bg-slate-700" data-testid={`wifi-mon-edit-${r.id}`}><Settings2 size={12} /></button>}
      </div>
      {open && <Details row={r} />}
    </div>
  );
}

export default function MonitoringPanel({ devices, canManage }) {
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [pick, setPick] = useState("");
  const load = useCallback(() => api.get("/wifi/monitoring").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/wifi/monitoring/profiles").then((r) => setProfiles(r.data)).catch(() => {}); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const free = devices.filter((d) => !d.monitoring?.enabled && d.ip);
  return (
    <div className="flex-1 overflow-y-auto" data-testid="wifi-monitoring">
      {canManage && (
        <div className="flex gap-2 p-3 border-b border-slate-800 text-[11px]">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className={`${inp} max-w-xs`} data-testid="wifi-mon-pick"><option value="">— urządzenie z IP do monitorowania —</option>{free.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.ip})</option>)}</select>
          <button disabled={!pick} onClick={() => setCfg(devices.find((d) => d.id === pick))} className="bg-purple-600 hover:bg-purple-500 disabled:opacity-40 rounded px-3" data-testid="wifi-mon-add">Włącz monitoring</button>
        </div>
      )}
      {rows.length === 0 && <div className="p-6 text-[11px] text-slate-500">Brak monitorowanych urządzeń. Wybierz nadajnik/CPE z adresem IP i włącz monitoring SNMP.</div>}
      {rows.map((r) => <Row key={r.id} r={r} canManage={canManage} onConfig={setCfg} onPolled={load} />)}
      {cfg && <ConfigForm dev={cfg} profiles={profiles} onClose={() => setCfg(null)} onSaved={() => { setCfg(null); setPick(""); load(); }} />}
    </div>
  );
}
